import styles from './admin-change-contract.module.scss';
import { Dispatch, useContext, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Col, Row } from 'reactstrap';
import { UseSignalRHook } from '../signalr-context/signalr-context';
import { useGetRequestNewHook, useMakeRequestHook } from '../requestUtils';
import { ValueFormatterParams } from 'ag-grid-community';
import { format } from 'date-fns';
import { Box, Container, CreateHeaders, dateHeader, DatePicker, HeaderSizeEnum, MenuItem, Option, OrderTypesEnum, PrimaryButton, Select, SnackBarContext, textHeader, Typography } from '@frontend-workspace/react-shared-lib';
import AdminChangeContractLicenseDialog from '../admin-change-contract-license-dialog/admin-change-contract-license-dialog';
import { IAdminContract } from '../_models/admin-contract';
import { IAdminContractLicense } from '../_models/admin-contract-license';
import WebkuaTable from '../webkua-table/webkua-table';
import AdminChangeContractSkeleton from './admin-change-contract-skeleton';
import { useTranslation } from 'react-i18next';

/* eslint-disable-next-line */
export interface AdminChangeContractProps {
  contract: IAdminContract,
  getData: () => void,
  setData: Dispatch<IAdminContract>,
  vismaIdOptions: Option[]
}

export function AdminChangeContract() {
  const { contractNumber } = useParams();
  const { data: contract, getData, setData }: { data: IAdminContract, getData: () => void, setData: Dispatch<IAdminContract> } = useGetRequestNewHook(`admin/contracts/${contractNumber}`, undefined, [contractNumber]);
  const { data: contractItemInfos } = useGetRequestNewHook(`contractItemInfos`, undefined, [contractNumber]);

  UseSignalRHook(getData, [OrderTypesEnum.AdminEditLicenseOrder]);
  const vismaItemOptions = contractItemInfos?.map((x: any) => ({ id: x.id, label: x.id }));

  return (
    contract ? <AdminChangeContractContent
      contract={contract}
      getData={getData}
      setData={setData}
      vismaIdOptions={vismaItemOptions}
    /> : <AdminChangeContractSkeleton />
  );
}

export function AdminChangeContractContent(props: AdminChangeContractProps) {
  const { t } = useTranslation();
  const makeRequest = useMakeRequestHook();
  const [nextInvoice, setNextInvoice] = useState(new Date(props.contract.nextInvoice));
  const [isActive, setIsActive] = useState(props.contract.isActive);
  const { setSnackbarMessage } = useContext(SnackBarContext);

  const [selectedLicense, setSelectedLicense] = useState<IAdminContractLicense | undefined>(undefined);
  const handleInvoiceChange = (date: Date) => setNextInvoice(date);
  const handleCloseDialog = () => {
    setSelectedLicense(undefined);
    props.getData();
  }
  const handleStatusChange = (event: any) => setIsActive(event.target.value);

  const HandleUpdateContract = () => makeRequest(`admin/action/updateContract/${props.contract.contractNumber}`, "PUT", {
    nextInvoice: format(nextInvoice, "yyyy-MM-dd"),
    isActive: isActive,
    contractNumber: props.contract.contractNumber
  }).then(result => {
    setSnackbarMessage({ content: t("adminChangeContracts.contractUpdated"), type: 'success' });
    props.getData();
  });

  const editLicenseRenderer = (params: ValueFormatterParams) => {
    const onClick = () => setSelectedLicense(params.data);
    return (
      <PrimaryButton
        variant='outlined'
        size='small'
        onClick={onClick}>
        {t("standard.change")}
      </PrimaryButton>
    );
  };

  const headers = CreateHeaders([
    textHeader("licenseNumber", t("standard.licenseNumber"), HeaderSizeEnum.MEDIUM, { renderer: 'agGroupCellRenderer' }),
    textHeader("parentLicenseNumber", t("standard.parentLicenseNumber"), HeaderSizeEnum.MEDIUM),
    textHeader("name", t("standard.product"), HeaderSizeEnum.EXTRA_LARGE),
    textHeader("type", "Type", HeaderSizeEnum.MEDIUM),
    textHeader("seats", t("standard.seats"), HeaderSizeEnum.MEDIUM),
    dateHeader("terminationDate", t("standard.terminationDate")),
    dateHeader("maintenanceExpirationDate", t("standard.expirationDate")),
    textHeader("licenseNumber", "", HeaderSizeEnum.MEDIUM, {
      renderer: editLicenseRenderer,
      cellStyle: { justifyContent: 'right' },
      headerClass: "ag-right-aligned-header"
    })
  ]);

  return (
    <Container
      className="container-fluid pt-4 px-0"
      maxWidth={false}
      sx={{ paddingBottom: '50px' }}>
      <Row>
        <Col>
          <Box
            component='div'
            className='pb-5'>
            <div className='mb-5'>
              <Typography
                variant='h2'
                component='span'
                sx={{ flexGrow: 1 }}>
                Kontraktnr. {props.contract.contractNumber}
              </Typography>
            </div>
            <div className="d-flex flex-row align-items-end flex-wrap">
              <div className="mb-3 mb-xxl-0 me-4">
                <Select
                  label={t("adminChangeContracts.contractStatus")}
                  name='contractStatus'
                  onChange={handleStatusChange}
                  value={isActive}>
                  <MenuItem key={"Active"} value={true}>{t("adminChangeContracts.active")}</MenuItem>
                  <MenuItem key={"Expired"} value={false}>{t("adminChangeContracts.expired")}</MenuItem>
                </Select>
              </div>
              <div className="mb-3 mb-xxl-0 me-5">
                <DatePicker
                  inputFormat="dd.MM.yyyy"
                  label={t("standard.nextInvoice")}
                  value={nextInvoice}
                  onChange={handleInvoiceChange}
                />
              </div>
              <div className="mb-3 mb-xxl-0">
                <PrimaryButton
                  onClick={HandleUpdateContract}
                  variant="contained">
                  <i className="fas fa-sync-alt"></i> {t("adminChangeContracts.updateContract")}
                </PrimaryButton>
              </div>
            </div>
            <WebkuaTable
              headers={headers}
              data={props.contract.licenses}
            />
            {selectedLicense && <AdminChangeContractLicenseDialog
              licenseNumber={selectedLicense.licenseNumber}
              vismaIdOptions={props.vismaIdOptions}
              onClose={handleCloseDialog}
            />}
          </Box>
        </Col>
      </Row>
    </Container>
  );
}

export default AdminChangeContract;